import { useQuery } from '@apollo/client';
import { notEmpty } from '@crehana/utils';

import type { AccessLevelEnum } from '@/shared/types/graphql/globals.b2b';
import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';
import useDebounce from '@/views/AdminDashboard/hooks/useDebounce';

import GET_COURSES_QUERY from './graphql/GetGroupDetailSearchCoursesMutation.b2b.graphql';
import type {
  GetGroupDetailSearchCoursesMutation,
  GetGroupDetailSearchCoursesMutationVariables,
} from './graphql/types/GetGroupDetailSearchCoursesMutation';

const COURSES_PER_FETCH = 12;

type TUseSearchCoursesArgs = {
  excludeIds: number[];
  accessLevelEnum: AccessLevelEnum | null;
  skip?: boolean;
};

const useSearchCourses = ({
  excludeIds = [],
  accessLevelEnum = null,
  skip,
}: TUseSearchCoursesArgs) => {
  const { organizationId } = useAdminDashboard();
  const { debounceValue, handleChange } = useDebounce();

  const { data, loading, fetchMore } = useQuery<
    GetGroupDetailSearchCoursesMutation,
    GetGroupDetailSearchCoursesMutationVariables
  >(GET_COURSES_QUERY, {
    variables: {
      search: debounceValue || '',
      first: COURSES_PER_FETCH,
      organizationId,
      accessLevelEnum,
    },
    fetchPolicy: 'network-only',
    skip: !debounceValue || skip,
    context: { clientName: 'b2b' },
  });

  const customFetchMoreData = () =>
    fetchMore({
      variables: {
        search: debounceValue || '',
        first: COURSES_PER_FETCH,
        after: data?.coursesSearch?.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (
          prev.coursesSearch?.edges &&
          fetchMoreResult?.coursesSearch?.edges &&
          fetchMoreResult.coursesSearch.pageInfo
        ) {
          return {
            ...prev.coursesSearch,
            coursesSearch: {
              ...prev.coursesSearch,
              edges: [
                // TODO: talk with the code owner to fix this code so we can enable this rule again
                // eslint-disable-next-line no-unsafe-optional-chaining
                // biome-ignore lint/correctness/noUnsafeOptionalChaining: optional chaining is safe in this context
                ...prev.coursesSearch?.edges,
                ...fetchMoreResult.coursesSearch.edges,
              ],
              pageInfo: {
                ...fetchMoreResult.coursesSearch.pageInfo,
              },
              __typename: prev.coursesSearch.__typename,
            },
          };
        }
        return prev;
      },
    });

  const courses =
    data?.coursesSearch?.edges
      ?.filter(notEmpty)
      .map(course => course.node)
      .filter(notEmpty)
      .filter(course => !excludeIds.includes(course?.originalId || 0)) || [];

  return {
    courses,
    loading,
    customFetchMoreData,
    hasNextPage: data?.coursesSearch?.pageInfo.hasNextPage,
    handleChange,
  };
};

export default useSearchCourses;
